import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiService from 'common/services/api/apiService';
import FetchStatus from 'common/types/FetchStatus';
import { AppThunk } from 'configureStore';
import initial from 'domain/initial';
import ChsProfile from 'domain/types/ChsProfile';

type ProfileState = {
  data: ChsProfile;
  meta: {
    fetchStatus: FetchStatus;
  };
};

export const initialState: ProfileState = {
  data: {
    address: initial.address(),
    firstName: '',
    lastName: '',
    suffix: '',
    mobileNum: '',
    dayNum: '',
    email: '',
    statusCd: '',
    statusDescr: '',
    licenseNumber: null,
    identity: null,
    verification: null,
  },
  meta: {
    fetchStatus: FetchStatus.none,
  },
};

const ProfileSlice = createSlice({
  name: 'Profile',
  initialState,
  reducers: {
    startLoading(state) {
      state.meta.fetchStatus = FetchStatus.loading;
      return state;
    },
    finishLoading(state, action: PayloadAction<ProfileState['data']>) {
      state.data = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        suffix: action.payload.suffix,
        address: action.payload.address,
        mobileNum: action.payload.mobileNum,
        dayNum: action.payload.dayNum,
        email: action.payload.email,
        licenseNumber: action.payload.licenseNumber,
        statusCd: action.payload.statusCd,
        statusDescr: action.payload.statusDescr,
        identity: action.payload.identity,
        verification: action.payload.verification,
      };
      state.meta.fetchStatus = FetchStatus.loaded;
      return state;
    },
    finishSavingLicense(
      state,
      action: PayloadAction<{ licenseNumber: number }>
    ) {
      state.data.licenseNumber = action.payload.licenseNumber;
      return state;
    },
  },
});

export function loadProfile(
  token?: string
): AppThunk<Promise<boolean | string>> {
  return async function (dispatch, getState) {
    const access_token = token || getState().auth.access_token;
    if (!access_token) {
      return Promise.reject('Invalid access token.');
    }

    let error;
    try {
      dispatch(ProfileSlice.actions.startLoading());
      const res = await apiService.get('/profile/userInfo', access_token);
      if (res.ok) {
        const profile = await res.json();
        dispatch(ProfileSlice.actions.finishLoading(profile));
        return Promise.resolve(true);
      }
      error = 'Unhandled error.';
    } catch (err) {
      error = err;
    }

    return Promise.reject(error);
  };
}

export function saveProfile(
  data: ProfileState['data']
): AppThunk<Promise<boolean | string>> {
  return async function (dispatch, getState) {
    dispatch(ProfileSlice.actions.startLoading());
    const { access_token } = getState().auth;
    let error;
    try {
      const res = await apiService.put(
        '/profile/updateProfile',
        access_token,
        data
      );
      if (res.ok) {
        const profile = await res.json();
        dispatch(ProfileSlice.actions.finishLoading(profile));
        return Promise.resolve(true);
      }
      error = 'Unhandled error.';
    } catch (err) {
      error = err;
    }

    return Promise.reject(error);
  };
}

export const { finishSavingLicense } = ProfileSlice.actions;

export default ProfileSlice.reducer;
